import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";

const inputClassName =
  "block w-full rounded-lg border border-zinc-200/90 bg-white px-4 py-3 text-base text-zinc-900 placeholder:text-zinc-400 shadow-sm transition-colors focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary";

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const from = location.state?.from?.pathname || "/";

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(formData.email, formData.password);
      navigate(from, { replace: true });
    } catch (err: any) {
      if (err.newPasswordRequired) {
        navigate("/new-password", {
          state: { email: formData.email, from },
        });
        return;
      }
      if (err.mfaRequired) {
        navigate("/verify-mfa", {
          state: { email: formData.email, from },
        });
        return;
      }
      setError(err.message || "Failed to login");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-start bg-zinc-50 px-4 pb-16 pt-12">
      <div className="flex w-full max-w-md flex-col items-center">
        <div className="mb-10 flex flex-col items-center">
          <img
            className="mb-4 h-24 w-24 object-contain"
            src="/sagelyn-circle.png"
            alt="Sagelyn"
            onError={(e) => {
              e.currentTarget.src =
                "https://via.placeholder.com/64x64?text=S";
            }}
          />
          <h1 className="text-lg font-semibold tracking-tight text-brand-primary">
            Sagelyn
          </h1>
          <p className="mt-1 text-sm text-zinc-500">Sign in to continue</p>
        </div>

        <div className="w-full rounded-xl border border-zinc-200/90 bg-white p-6 shadow-sm sm:p-8">
          <form className="w-full space-y-5" onSubmit={handleSubmit}>
            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                <div className="text-sm text-red-800">{error}</div>
              </div>
            )}

            <div>
              <label
                htmlFor="email"
                className="mb-1.5 block text-sm font-medium text-zinc-700"
              >
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                placeholder="you@company.com"
                className={inputClassName}
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            <div>
              <div className="mb-1.5 flex items-center justify-between gap-2">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-zinc-700"
                >
                  Password
                </label>
                <button
                  type="button"
                  onClick={() => navigate("/forgot-password")}
                  className="text-sm font-medium text-brand-primary-light hover:text-brand-primary focus:outline-none focus-visible:underline"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  className={`${inputClassName} pr-12`}
                  value={formData.password}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 focus:outline-none"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <FaEyeSlash className="h-4 w-4" />
                  ) : (
                    <FaEye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="flex w-full justify-center rounded-lg bg-brand-primary px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-primary-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>

        <p className="mt-10 text-center text-xs text-zinc-500">
          Powered by DaisyAI, LLC
        </p>
      </div>
    </div>
  );
};

export default Login;
